import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Checkbox, Chip, FormControl, Grid, IconButton, InputLabel, LinearProgress, Link, ListItemText, MenuItem, Paper,
  Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, Info as InfoIcon, ViewKanban as L2Icon
} from '@mui/icons-material';
import { authGet } from '../../services/apiService';
import VitalSignsQueryLink from './VitalSignsQueryLink';
import OpenAgeCard, { EMPTY_AGE_COUNTS } from './OpenAgeCard';
import { customerNames } from './ticketListHelpers';

const L2_COLOR = '#2E7D32';
const COLUMNS = [
  { field: 'id', label: 'ADO ID' },
  { field: 'title', label: 'Title' },
  { field: 'state', label: 'State' },
  { field: 'priority', label: 'Priority' },
  { field: 'cases', label: 'SF Case(s)' },
  { field: 'customer', label: 'Customer' },
  { field: 'assignedTo', label: 'Assigned to' },
  { field: 'age', label: 'Age (days)', align: 'right' }
];

const adoUrl = (id) => {
  const org = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
  const project = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
  const base = org.includes('visualstudio.com') ? `https://${org}` : `https://dev.azure.com/${org}`;
  return `${base}/${encodeURIComponent(project)}/_workitems/edit/${id}`;
};

const ageColor = (age) => {
  if (age == null) return 'default';
  if (age > 180) return '#d32f2f';
  if (age > 90) return '#f57c00';
  if (age > 30) return '#fbc02d';
  return '#388e3c';
};

const LINK_FILTERS = [
  ['open', 'Linked to open L1 case'],
  ['none', 'Not linked']
];
const linkKind = (ticket) => (ticket.cases.length > 0 ? 'open' : 'none');

const ALL_PRIORITIES = [1, 2, 3, 4];
const EMPTY_COUNTS = { byPriority: {}, byState: {}, byAge: EMPTY_AGE_COUNTS, oldestAge: null };
// A ticket with no priority is stored as P3. Only the list's Priority filter uses this; the box counts come from the server.
const priorityOf = (ticket) => (ticket.priority == null ? 3 : Number(ticket.priority));

const sortValue = (ticket, field) => {
  if (field === 'customer') return customerNames(ticket);
  if (field === 'cases') return ticket.cases.map((c) => c.id).join(',');
  return ticket[field];
};

const PRIORITY_COLUMNS = [
  { priority: 1, label: 'P1 Critical', color: '#c62828' },
  { priority: 2, label: 'P2 High', color: '#e65100' },
  { priority: 3, label: 'P3 Medium', color: '#555555' },
  { priority: 4, label: 'P4 Low', color: '#2e7d32' }
];

// States shown in the box; counts are over the tickets the Open number counts.
const BOX_STATES = ['Blocked', 'New', 'Active'];

const SummaryCard = ({ title, info, open, byPriority, priorities, byState }) => (
  <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, pl: 2, pr: 0.5, py: 0.5,
      background: `linear-gradient(135deg, ${L2_COLOR}ee 0%, ${L2_COLOR}bb 100%)`
    }}>
      <L2Icon sx={{ color: '#fff' }} />
      <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 0.3, flexGrow: 1 }}>
        {title}
      </Typography>
      <VitalSignsQueryLink tile="l2Open" />
      <Tooltip title={info} arrow placement="top">
        <IconButton size="small" aria-label={`About ${title}`}><InfoIcon fontSize="small" sx={{ color: '#fff' }} /></IconButton>
      </Tooltip>
    </Box>
    <Box sx={{ p: 1.75 }}>
      <Box sx={{ textAlign: 'center', pb: 1.25 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: L2_COLOR, lineHeight: 1 }}>{open}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Open</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-around', borderTop: '1px solid', borderColor: 'divider', pt: 1.25 }}>
        {PRIORITY_COLUMNS.filter(({ priority }) => priorities.includes(priority)).map(({ priority, label, color }) => (
          <Box key={priority} sx={{ textAlign: 'center', px: 0.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{byPriority[priority] || 0}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontWeight: 500 }}>{label}</Typography>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary' }}>
        BY STATE
      </Typography>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mt: 0.5 }}>
        {BOX_STATES.map((state, index) => (
          <Box
            key={state}
            sx={{ display: 'flex', px: 1.5, py: 0.6, ...(index > 0 ? { borderTop: '1px solid', borderColor: 'divider' } : null) }}
          >
            <Typography variant="caption" sx={{ flex: 1 }}>{state}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: L2_COLOR }}>{byState[state] || 0}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Paper>
);

/** Open L2 tickets (Investigations in the TSI area, not Done), with the open Salesforce cases each is tied to. */
const OpenL2IncidentsView = () => {
  const [tickets, setTickets] = useState(null);
  const [definition, setDefinition] = useState('');
  const [openCount, setOpenCount] = useState(0);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [configPriorities, setConfigPriorities] = useState(ALL_PRIORITIES);
  const [selectedPriorities, setSelectedPriorities] = useState([]); // empty = every priority
  const [error, setError] = useState(null);
  const [selectedState, setSelectedState] = useState('');
  const [selectedLink, setSelectedLink] = useState('');
  const [ageBucket, setAgeBucket] = useState('');
  const [sortField, setSortField] = useState('age');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    let cancelled = false;
    authGet('/api/customer-issues/vital-signs-l2-open')
      .then((response) => {
        if (cancelled) return;
        if (!response.success) throw new Error(response.message || 'Failed to load open L2 tickets');
        setTickets(response.tickets || []);
        setOpenCount(response.openCount || 0);
        setCounts(response.counts || EMPTY_COUNTS);
        // The scope never removes tickets from the list: it only starts the list filtered to the linked tickets.
        if (response.scope === 'linkedOpenL1') setSelectedLink('open');
        // Same for the priorities: all tickets stay in the list, it starts filtered to the included priorities.
        const included = response.priorities || ALL_PRIORITIES;
        setConfigPriorities(included);
        if (included.length < ALL_PRIORITIES.length) setSelectedPriorities(included);
        setDefinition(response.definition || '');
      })
      .catch((err) => {
        console.error('Error fetching open L2 tickets:', err);
        if (!cancelled) setError('Failed to load open L2 tickets. Please try again later.');
      });
    return () => { cancelled = true; };
  }, []);

  const all = useMemo(() => tickets || [], [tickets]);
  const byState = useMemo(() => {
    const counts = {};
    all.forEach((ticket) => { counts[ticket.state || 'Unknown'] = (counts[ticket.state || 'Unknown'] || 0) + 1; });
    return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  }, [all]);


  const rows = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return all
      .filter((ticket) => !selectedState || ticket.state === selectedState)
      .filter((ticket) => !selectedLink || linkKind(ticket) === selectedLink)
      .filter((ticket) => selectedPriorities.length === 0 || selectedPriorities.includes(priorityOf(ticket)))
      .filter((ticket) => !ageBucket || ticket.ageBucket === ageBucket)
      .sort((a, b) => {
        const va = sortValue(a, sortField);
        const vb = sortValue(b, sortField);
        if (['age', 'id', 'priority'].includes(sortField)) {
          // A ticket with no value (e.g. no opened date) always sorts last, whichever way the list is sorted.
          const aMissing = va == null || Number.isNaN(Number(va));
          const bMissing = vb == null || Number.isNaN(Number(vb));
          if (aMissing || bMissing) return Number(aMissing) - Number(bMissing);
          return dir * (Number(va) - Number(vb));
        }
        return dir * String(va || '').toLowerCase().localeCompare(String(vb || '').toLowerCase());
      });
  }, [all, selectedState, selectedLink, selectedPriorities, ageBucket, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDirection('asc'); }
  };

  if (!tickets && !error) return <LinearProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="L2 Open"
            info={[
              definition,
              'This is the rule set in Admin > Ops Center > Vital Signs Config.',
              'Open is the number that rule counts and is the number on the L2 Open tile.',
              'The list below always shows every open L2 ticket.',
              'When the rule counts only tickets linked to an open L1 ticket, or only some priorities,',
              'the list starts filtered to those; clear the L1 Case Status and Priority filters to see them all.',
              'The priority counts split the Open number by the priorities the rule includes,',
              'and the state table shows how many of the Open tickets are Blocked, New or Active.',
              'The SF Case(s) column lists the open Salesforce cases that link to a ticket.'
            ].join(' ')}
            open={openCount}
            byPriority={counts.byPriority}
            priorities={configPriorities}
            byState={counts.byState}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <OpenAgeCard
            counts={counts.byAge}
            oldestDays={counts.oldestAge}
            selectedBucket={ageBucket}
            onSelectBucket={setAgeBucket}
          />
        </Grid>
      </Grid>

      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Open L2 Incidents</Typography>
          <Typography variant="caption" color="text.secondary">
            Every open L2 ticket (states per the L2 Open rule), with the open Salesforce cases that link to each.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>State</InputLabel>
            <Select value={selectedState} label="State" onChange={(e) => setSelectedState(e.target.value)}>
              <MenuItem value="">All States</MenuItem>
              {byState.map(([state]) => <MenuItem key={state} value={state}>{state}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            {/* shrink + notched: the "All priorities" text shows with no selection, so the label must sit on the border */}
            <InputLabel shrink>Priority</InputLabel>
            <Select
              multiple
              notched
              value={selectedPriorities}
              label="Priority"
              onChange={(e) => setSelectedPriorities(e.target.value)}
              renderValue={(value) => (value.length === 0 ? 'All priorities' : value.map((p) => `P${p}`).join(', '))}
              displayEmpty
            >
              {ALL_PRIORITIES.map((priority) => (
                <MenuItem key={priority} value={priority}>
                  <Checkbox size="small" checked={selectedPriorities.includes(priority)} />
                  <ListItemText primary={`P${priority}`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>L1 Case Status</InputLabel>
            <Select value={selectedLink} label="L1 Case Status" onChange={(e) => setSelectedLink(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {LINK_FILTERS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {rows.length} of {all.length} tickets
            </Typography>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.field}
                    align={col.align}
                    sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort(col.field)}
                  >
                    <Box display="flex" alignItems="center" justifyContent={col.align === 'right' ? 'flex-end' : 'flex-start'}>
                      {col.label}
                      {sortField === col.field && (sortDirection === 'asc'
                        ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />)}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {all.length === 0 ? 'No open L2 tickets.' : 'No tickets found matching the selected filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((ticket) => (
                <TableRow key={ticket.id} hover>
                  <TableCell>
                    <Link href={adoUrl(ticket.id)} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                      #{ticket.id}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 400 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{ticket.title || 'Untitled'}</Typography>
                  </TableCell>
                  <TableCell><Chip label={ticket.state || 'Unknown'} size="small" variant="outlined" /></TableCell>
                  <TableCell>{ticket.priority != null ? <Chip label={`P${ticket.priority}`} size="small" variant="outlined" /> : '-'}</TableCell>
                  <TableCell>
                    {ticket.cases.map((c, index) => (
                      <span key={c.id}>
                        {index > 0 && ', '}
                        {c.url
                          ? <Link href={c.url} target="_blank" rel="noopener noreferrer" underline="hover">{c.id}</Link>
                          : c.id}
                      </span>
                    ))}
                    {ticket.cases.length === 0 && '—'}
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{customerNames(ticket) || 'Unknown'}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{ticket.assignedTo || 'Unassigned'}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title={ticket.createdDate ? new Date(ticket.createdDate).toLocaleString() : 'Created date unknown'}>
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        {ticket.age != null
                          ? <Chip label={`${ticket.age} days`} size="small" sx={{ backgroundColor: ageColor(ticket.age), color: 'white', fontWeight: 'bold' }} />
                          : <Typography variant="body2" color="text.secondary">-</Typography>}
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default OpenL2IncidentsView;
